import React, { useCallback, useEffect, useState } from 'react';
import { useSente, SenteMessage } from '../hooks/useSente';

type DirectionFilter = '' | 'inbound' | 'outbound';
type KindFilter = '' | 'otp' | 'magic_link' | 'other';

const pillClass = (active: boolean) =>
  `px-3 py-1 text-xs font-medium rounded-full transition-colors ${
    active
      ? 'bg-blue-600 text-white'
      : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
  }`;

const deliveryBadge = (status: string) => {
  const colors: Record<string, string> = {
    sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    bounced: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    complained: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return colors[status] || 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400';
};

interface IdentityInboxProps {
  // null = org-wide: every identity's mail (Activity's Emails tab). A string scopes to one identity.
  identityId: string | null;
  email: string;
  // Org-wide mode: identity id → email, so each row can say which identity it belongs to.
  emailById?: Map<string, string>;
  // When > 0, auto-refresh on this interval (ms) — used on the run page so a verification
  // email appears without a manual refresh. Omit/0 for manual-refresh-only (identity page).
  pollMs?: number;
  // Bump to force a re-fetch from the parent (e.g. after it sends an outbound message).
  refreshSignal?: number;
}

/**
 * The inbox: direction/kind filters, message list with inline OTP/magic-link annotations, and
 * lazy-expanded bodies. Self-contained (owns its own fetch + state) so it can be dropped into the
 * identity page, the run page (live side panel), and Activity (org-wide, `identityId={null}`).
 */
const IdentityInbox: React.FC<IdentityInboxProps> = ({
  identityId,
  email,
  emailById,
  pollMs = 0,
  refreshSignal = 0,
}) => {
  const { listMessages, getMessage, archiveMessage, unarchiveMessage } = useSente();

  const [messages, setMessages] = useState<SenteMessage[]>([]);
  const [loaded, setLoaded] = useState(false); // first fetch done — gate the empty state until then
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<DirectionFilter>('');
  const [kind, setKind] = useState<KindFilter>('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [fullMessages, setFullMessages] = useState<Record<string, SenteMessage>>({});
  const [loadingBodyId, setLoadingBodyId] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // `spinner` drives the manual Refresh button's spinner only — background polls fetch silently so
  // the header doesn't blink every interval.
  const fetchMessages = useCallback(async (spinner = false) => {
    if (spinner) setIsRefreshing(true);
    setError(null);
    try {
      setMessages(
        await listMessages(identityId, {
          limit: 100,
          direction: direction || undefined,
          kind: kind || undefined,
          includeArchived: showArchived,
        })
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load messages');
    } finally {
      setLoaded(true);
      if (spinner) setIsRefreshing(false);
    }
  }, [identityId, listMessages, direction, kind, showArchived]);

  // Archive/unarchive, then re-list (an archived message drops out of the default view).
  const toggleArchive = async (e: React.MouseEvent, msg: SenteMessage) => {
    e.stopPropagation();
    try {
      await (msg.archived ? unarchiveMessage(msg.id) : archiveMessage(msg.id));
      fetchMessages();
    } catch {
      // ponytail: transient failure — the next refresh reconciles
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Optional live refresh (run page): re-list on the interval so an inbound OTP/magic-link shows up.
  useEffect(() => {
    if (!pollMs) return;
    const t = setInterval(fetchMessages, pollMs);
    return () => clearInterval(t);
  }, [pollMs, fetchMessages]);

  // Parent-triggered refresh (e.g. after sending). Guarded so the initial 0 doesn't double-fetch.
  useEffect(() => {
    if (refreshSignal) fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const copy = (e: React.MouseEvent, key: string, value: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1500);
  };

  const toggleExpand = async (msg: SenteMessage) => {
    if (expandedId === msg.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(msg.id);
    if (!fullMessages[msg.id] && !msg.parsed) {
      setLoadingBodyId(msg.id);
      try {
        const full = await getMessage(msg.id);
        setFullMessages(prev => ({ ...prev, [msg.id]: full }));
      } catch {
        // ponytail: body fetch failure just falls back to "(no body)"
      } finally {
        setLoadingBodyId(null);
      }
    }
  };

  const renderBody = (msg: SenteMessage) => {
    if (loadingBodyId === msg.id) {
      return <div className="text-sm text-gray-500 dark:text-slate-400">Loading…</div>;
    }
    const full = fullMessages[msg.id] || msg;
    if (full.parsed?.text) {
      return <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-slate-200">{full.parsed.text}</pre>;
    }
    if (full.parsed?.html) {
      return <div className="text-sm italic text-gray-500 dark:text-slate-400">(HTML body — no text version)</div>;
    }
    return <div className="text-sm italic text-gray-500 dark:text-slate-400">(no body)</div>;
  };

  return (
    <div>
      {/* data-tour on the bounded filter bar, not the (potentially viewport-tall) whole list */}
      <div data-tour="inbox" className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button className={pillClass(direction === '')} onClick={() => setDirection('')}>All</button>
            <button className={pillClass(direction === 'inbound')} onClick={() => setDirection('inbound')}>Inbound</button>
            <button className={pillClass(direction === 'outbound')} onClick={() => setDirection('outbound')}>Outbound</button>
          </div>
          <div className="w-px h-4 bg-gray-200 dark:bg-slate-700" />
          <div className="flex items-center gap-1.5">
            <button className={pillClass(kind === '')} onClick={() => setKind('')}>All</button>
            <button className={pillClass(kind === 'otp')} onClick={() => setKind('otp')}>OTP</button>
            <button className={pillClass(kind === 'magic_link')} onClick={() => setKind('magic_link')}>Magic link</button>
            <button className={pillClass(kind === 'other')} onClick={() => setKind('other')}>Other</button>
          </div>
          <div className="w-px h-4 bg-gray-200 dark:bg-slate-700" />
          {/* Toggle: off = archived hidden (default), on = archived shown too (dimmed + badged). */}
          <button className={pillClass(showArchived)} onClick={() => setShowArchived(v => !v)}>
            Show archived
          </button>
        </div>
        <button
          onClick={() => fetchMessages(true)}
          disabled={isRefreshing}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
        >
          {isRefreshing && (
            <div className="animate-spin w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full" />
          )}
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {!loaded ? (
        // Hold a quiet loader until the first fetch resolves, so the empty state never flashes
        // before the messages arrive.
        <div className="flex items-center justify-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800">
          <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 px-6 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800">
          <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No messages yet</h3>
          <p className="text-gray-500 dark:text-slate-400 break-words">
            {identityId ? (
              <>Email <span className="font-mono">{email}</span> from anywhere and it will appear here.</>
            ) : (
              <>Mail to or from any of your identities shows up here.</>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map(msg => {
            const inbound = msg.direction === 'inbound';
            const counterparty = inbound ? msg.fromAddr : msg.toAddr;
            const ann = msg.annotation;
            const expanded = expandedId === msg.id;
            const full = fullMessages[msg.id] || msg;
            return (
              <div
                key={msg.id}
                className={`bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-700 transition-colors ${
                  msg.archived ? 'opacity-60' : ''
                }`}
              >
                <div
                  onClick={() => toggleExpand(msg)}
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer flex-wrap"
                >
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                      inbound
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                    }`}
                  >
                    {inbound ? '↓ in' : '↑ out'}
                  </span>
                  {/* Org-wide: whose inbox this landed in. */}
                  {!identityId && (
                    <span
                      className="px-2 py-0.5 text-xs font-mono rounded bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 flex-shrink-0 truncate max-w-[180px]"
                      title={emailById?.get(msg.identityId) ?? msg.identityId}
                    >
                      {emailById?.get(msg.identityId) ?? msg.identityId}
                    </span>
                  )}
                  {/* Spell out the direction word ("to …" on outbound) — "who did the agent email"
                      is the question customers actually ask, and a bare address doesn't answer it. */}
                  <span className="text-sm text-gray-600 dark:text-slate-400 truncate max-w-[240px]" title={counterparty}>
                    <span className="text-gray-400 dark:text-slate-500">{inbound ? 'from ' : 'to '}</span>
                    <span className={`font-mono ${inbound ? '' : 'font-medium text-purple-700 dark:text-purple-400'}`}>
                      {counterparty}
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1 min-w-[120px]">
                    {msg.subject || '(no subject)'}
                  </span>
                  {ann && ann.kind === 'otp' && ann.code && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs flex-shrink-0">
                      <span className="font-mono font-semibold text-green-700 dark:text-green-400">{ann.code}</span>
                      <button
                        onClick={e => copy(e, `otp-${msg.id}`, ann.code!)}
                        className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                        title="Copy code"
                      >
                        {copiedKey === `otp-${msg.id}` ? 'Copied' : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </span>
                  )}
                  {ann && ann.kind === 'magic_link' && ann.link && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded text-xs text-indigo-700 dark:text-indigo-400 flex-shrink-0">
                      Magic link
                      <button
                        onClick={e => copy(e, `link-${msg.id}`, ann.link!)}
                        className="hover:text-indigo-900 dark:hover:text-indigo-300"
                        title="Copy link"
                      >
                        {copiedKey === `link-${msg.id}` ? 'Copied' : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        )}
                      </button>
                    </span>
                  )}
                  {!inbound && msg.deliveryStatus && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${deliveryBadge(msg.deliveryStatus)}`}>
                      {msg.deliveryStatus}
                    </span>
                  )}
                  {msg.archived && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400">
                      Archived
                    </span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-slate-500 flex-shrink-0 ml-auto">
                    {new Date(msg.createdAt).toLocaleString()}
                  </span>
                  <button
                    onClick={e => toggleArchive(e, msg)}
                    className="p-1 text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors flex-shrink-0"
                    title={msg.archived ? 'Unarchive' : 'Archive'}
                  >
                    {msg.archived ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 6l2-2m0 0l2 2m-2-2v6" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                    )}
                  </button>
                </div>

                {expanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-slate-800 pt-3">
                    <div className="text-xs text-gray-500 dark:text-slate-400 space-y-0.5 mb-3">
                      <div><span className="font-medium text-gray-700 dark:text-slate-300">From:</span> <span className="font-mono">{full.fromAddr}</span></div>
                      <div><span className="font-medium text-gray-700 dark:text-slate-300">To:</span> <span className="font-mono">{full.toAddr}</span></div>
                      <div><span className="font-medium text-gray-700 dark:text-slate-300">Subject:</span> {full.subject || '(no subject)'}</div>
                      <div><span className="font-medium text-gray-700 dark:text-slate-300">Date:</span> {new Date(full.createdAt).toLocaleString()}</div>
                    </div>
                    {renderBody(msg)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IdentityInbox;
